import type { CreateFormData } from "@/components/create/types";
import {
  evaluateDesignBriefConfidence,
  type DesignBriefConfidence,
} from "@/lib/agent/designBriefReadiness";
import { committedDesignText } from "@/lib/agent/designIntent";
import {
  extractAvoidRequirements,
  stripAvoidRequirementClauses,
} from "@/lib/agent/designDescription";
import { escapeRegExp } from "@/lib/regex";

export interface DerivedDesignBrief {
  occasion?: string;
  front: {
    prompt?: string;
    color: string[];
    length?: string;
    sleeve?: string;
    sleeveAddons: string[];
    neckline?: string;
    waistline?: string;
    silhouette?: string;
    fabric: string[];
  };
  back: {
    prompt?: string;
    references: string[];
  };
  constraints: {
    avoid: string[];
    mustHave: string[];
    comfort: string[];
    modesty?: CreateFormData["modestyPreference"];
    budget: string[];
    mobility: string[];
  };
  references: {
    front: string[];
    back: string[];
    inspiration: string[];
  };
  confidence: DesignBriefConfidence;
  history: {
    lastConfirmed: string[];
    lastChanged: string[];
    unresolvedConflicts: string[];
  };
}

const AVOID_CANDIDATES = [
  "deep neckline",
  "heavy appliqué",
  "heavy fabric",
  "black",
  "white",
  "ivory",
  "gold",
  "champagne",
  "strapless",
  "mermaid",
  "ball gown",
  "princess",
  "lace",
  "sequins",
  "sparkle",
  "beading",
  "embroidery",
  "sleeves",
  "train",
  "slit",
  "plunge",
  "corset",
  "tight",
  "heavy",
  "itchy",
  "revealing",
];

const MUST_HAVE_CANDIDATES = [
  "pockets",
  "sleeves",
  "support",
  "coverage",
  "open back",
  "transparent back",
  "high collar",
  "high neckline",
  "ankle length",
];

function compactText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function listFromValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(listFromValue);
  }
  const text = compactText(value);
  if (!text) return [];
  return text
    .split(/\s*(?:,|\/|\band\b|\+)\s*/i)
    .map(part => part.trim())
    .filter(Boolean);
}

function urlListFromValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(urlListFromValue);
  }
  const text = compactText(value);
  return text ? [text] : [];
}

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = value.toLowerCase().replace(/\s+/g, " ").trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(value.trim());
  }
  return result;
}

function extractCandidatesByContext(
  text: string,
  candidates: string[],
  intentPattern: string,
  trailingIntentPattern: string
): string[] {
  const normalizedText = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[-]+/g, " ");
  return candidates.filter(candidate => {
    const escaped = escapeRegExp(
      candidate
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
    );
    const before = new RegExp(
      `\\b(?:${intentPattern})(?:\\s+\\w+){0,5}\\s+${escaped}\\b`,
      "i"
    );
    const after = new RegExp(
      `\\b${escaped}\\b(?:\\s+\\w+){0,5}\\s+(?:${trailingIntentPattern})\\b`,
      "i"
    );
    return before.test(normalizedText) || after.test(normalizedText);
  });
}

function extractAvoid(text: string): string[] {
  const extracted = unique([
    ...extractAvoidRequirements(text),
    ...extractCandidatesByContext(
      stripAvoidRequirementClauses(text),
      AVOID_CANDIDATES,
      "no|not|non|without|avoid|exclude|remove|drop|dislike|hate|dont like|don t like|do not like|not into|dont want|don t want|do not want|other than|except|anything but|instead of",
      "removed|excluded|out"
    ),
  ]);
  // Prefer the customer's scoped phrase over a broad token that happens to be
  // contained within it: "no heavy appliqué" is about the embellishment, not a
  // blanket rejection of every substantial fabric or construction choice.
  return extracted.filter(candidate => {
    const candidateWords = candidate.toLowerCase().split(/\s+/);
    return !extracted.some(other => {
      const otherWords = other.toLowerCase().split(/\s+/);
      return (
        otherWords.length > candidateWords.length &&
        candidateWords.every(word => otherWords.includes(word))
      );
    });
  });
}

function extractMustHave(text: string): string[] {
  const negativeIntent =
    "no|not|non|without|avoid|exclude|remove|drop|dislike|hate|dont like|don t like|do not like|not into|dont want|don t want|do not want|other than|except|anything but|instead of";
  const contextual = extractCandidatesByContext(
    text,
    MUST_HAVE_CANDIDATES,
    "must have|needs?|need|include|with|want|keep|make sure",
    "required|needed|must"
  );
  const normalizedText = text.toLowerCase().replace(/[-]+/g, " ");
  const negated = new Set(
    extractCandidatesByContext(
      normalizedText,
      MUST_HAVE_CANDIDATES,
      negativeIntent,
      "removed|excluded|out"
    ).map(candidate => candidate.toLowerCase())
  );
  const explicitlyPresent = MUST_HAVE_CANDIDATES.filter(candidate => {
    const isPresent = new RegExp(
      `\\b${escapeRegExp(candidate.toLowerCase())}\\b`,
      "i"
    ).test(normalizedText);
    if (!isPresent) return false;

    // A negative requirement belongs in `avoid`, never in `mustHave`.
    // Without this guard, "no sleeves" became both must-have sleeves and
    // avoid sleeves, and "no train" was represented as a positive train token.
    return !negated.has(candidate.toLowerCase());
  });
  return unique([...contextual, ...explicitlyPresent]).filter(
    candidate => !negated.has(candidate.toLowerCase())
  );
}

function extractByPatterns(text: string, patterns: Array<[RegExp, string]>) {
  return patterns
    .filter(([pattern]) => pattern.test(text))
    .map(([, label]) => label);
}

function getReferenceUrls(
  formState: Partial<CreateFormData>,
  side: "front" | "back"
) {
  const record = formState as Record<string, unknown>;
  const urls =
    side === "front"
      ? urlListFromValue(record.uploadedFrontImageUrls)
      : urlListFromValue(record.uploadedBackImageUrls);
  return unique(urls);
}

export function deriveDesignBrief(
  formState: Partial<CreateFormData>,
  options: {
    contextText?: string;
    hasFrontImage?: boolean;
    hasBackImage?: boolean;
  } = {}
): DerivedDesignBrief {
  const prompt = compactText(formState.prompt);
  const durablePromptText = committedDesignText(prompt);
  const durableContextText = committedDesignText(options.contextText);
  const contextText = [durablePromptText, durableContextText]
    .map(compactText)
    .filter(Boolean)
    .join(" ");
  const lowerContext = contextText.toLowerCase();
  const frontReferences = getReferenceUrls(formState, "front");
  const backReferences = getReferenceUrls(formState, "back");

  return {
    occasion: compactText(formState.preset_type || undefined),
    front: {
      prompt: compactText(durablePromptText),
      color: listFromValue(formState.color),
      length: compactText(formState.length),
      sleeve: compactText(formState.sleeve),
      sleeveAddons: listFromValue(formState.sleeveAddons),
      neckline: compactText(formState.neckline),
      waistline: compactText(formState.waistline),
      silhouette: compactText(formState.silhouette),
      fabric: listFromValue(formState.fabric),
    },
    back: {
      prompt: compactText(formState.backPrompt),
      references: backReferences,
    },
    constraints: {
      avoid: extractAvoid(contextText),
      mustHave: extractMustHave(contextText),
      comfort: extractByPatterns(lowerContext, [
        [/\bcomfortable|comfort|easy to wear|not restrictive\b/i, "comfort"],
        [/\bsupport|supportive|bra|shapewear\b/i, "support"],
        [/\blightweight|not heavy|breathable|cool\b/i, "wearability"],
      ]),
      modesty: formState.modestyPreference,
      budget: extractByPatterns(lowerContext, [
        [
          /\bbudget|price[-\s]?smart|cost[-\s]?conscious|affordable\b/i,
          "budget",
        ],
        [
          /\bno embroidery|no beading|less complex|simple construction\b/i,
          "lower construction cost",
        ],
      ]),
      mobility: extractByPatterns(lowerContext, [
        [/\bdance|dancing\b/i, "dance"],
        [/\bstairs?|walking|walk|mobility|move\b/i, "movement"],
        [/\boutdoor|outside|grass|beach|garden\b/i, "outdoor"],
        [/\ball day|all night|many hours|long wear\b/i, "long wear"],
      ]),
    },
    references: {
      front: frontReferences,
      back: backReferences,
      inspiration: unique([...frontReferences, ...backReferences]),
    },
    confidence: evaluateDesignBriefConfidence(
      prompt === durablePromptText
        ? formState
        : { ...formState, prompt: durablePromptText || undefined },
      {
        contextText: durableContextText,
      }
    ),
    history: {
      lastConfirmed: [
        ...(options.hasFrontImage ? ["front image"] : []),
        ...(options.hasBackImage ? ["back image"] : []),
      ],
      lastChanged: [],
      unresolvedConflicts: [],
    },
  };
}
